import _ from 'underscore';
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Controls, ControlsBlock } from 'components/Controls';
import FilterButton from 'components/FilterButton';
import Search from 'components/filters/Search';

import { useDeepEffect } from 'hooks/useDeepEffect';

import FilterModal from 'components/filters/FilterModal';
import MobileSelect from 'components/filters/MobileSelect';
import Switch from 'components/filters/Switch';
import { filtersValuesPropType } from 'pageTemplates/institutes/propTypes';
import { sortOptions } from 'components/filters/SortFilters';
import MobileList from './components/MobileList';

import styles from './styles.module.scss';

const museumsSwitchFilters = [
	{ name: 'isCommercial', label: 'Частные музеи' },
	{ name: 'hasVirtualTours', label: 'Виртуальные музеи' },
	{ name: 'hasInfographic', label: 'Инфографика' }
];

const MobileInstituteControls = ({
	onFilterChange,
	filters,
	filtersValues,
	onFilterReset,
	defaultFiltersValues,
	filtersLoading,
	rubricPath,
	showSortFilter = true
}) => {
	const [isModalOpened, setIsModalOpened] = useState(false);
	const [filterData, setFilterData] = useState(null);
	const [filterStatus, setFilterStatus] = useState(false);
	const [filterList, setFilterList] = useState({});
	const [filterOptions, setFilterOptions] = useState({});

	useEffect(() => {
		setFilterOptions(filters);
	}, [filters]);

	const setFilter = useCallback(
		(filterName, filterValue) => {
			setFilterStatus(false);

			onFilterChange(filterName, filterValue);
			setFilterData(null);
		},
		[onFilterChange]
	);

	const museumsFilters = museumsSwitchFilters.reduce((acc, curr) => {
		if (filters[curr.name]) {
			acc.push(curr.label);
		}
		return acc;
	}, []);

	useDeepEffect(() => {
		setFilterList({
			location: {
				name: 'location',
				options: filtersValues.locales,
				resetOption: {
					title: 'По всей России',
					sysName: defaultFiltersValues.location,
					titleGenitive: 'России'
				},
				controlTitleField: 'title',
				label: 'Местоположение',
				valueField: 'sysName',
				accent: true,
				searchable: true,
				selectedOption: filtersValues?.currentLocale,
				onChange: setFilter
			},
			...(rubricPath === 'architecture/institutes' && {
				architect: {
					apiParams: {
						url: 'persons',
						dataPath: 'items',
						debounce: 300,
						affectQueryParams: {
							_ids: !_.isEmpty(filtersValues?.architectsIds)
								? filtersValues?.architectsIds
								: null,
							sort: 'surname,title',
							fields: ['title', 'name'].join(),
							limit: 100
						},
						paginationType: 'pagination'
					},
					controlTitleField: 'title',
					name: 'architect',
					label: 'Архитектор',
					options: filtersValues.architects,
					onChange: setFilter,
					resetOption: { title: 'Все архитекторы', name: '' },
					searchable: true,
					selectedValue: filters?.architect,
					valueField: 'name'
				}
			}),
			...(rubricPath === 'literature/libraries' && {
				subordinationType: {
					controlTitleField: 'title',
					name: 'subordinationType',
					label: 'Тип подчинения',
					options: filtersValues?.subordinationTypes,
					onChange: setFilter,
					selectedValue: filters?.subordinationType,
					valueField: 'name'
				}
			}),
			...(rubricPath === 'music/virtual-concert-halls' && {
				type: {
					controlTitleField: 'title',
					name: 'type',
					label: 'Тип',
					options: filtersValues?.instituteTypes,
					onChange: setFilter,
					selectedValue: filters?.type,
					valueField: 'name'
				}
			}),
			...(rubricPath === 'museums/institutes' && {
				else: {
					type: 'switches',
					name: 'else',
					label: 'Еще',
					filters: museumsSwitchFilters,
					resetOption: {
						title: _.isEmpty(museumsFilters)
							? 'Все музеи'
							: museumsFilters.join(', ')
					},
					valueField: 'name',
					controlTitleField: 'title'
				}
			}),
			...(showSortFilter && {
				sort: {
					options: sortOptions,
					name: 'sort',
					selectedValue: filters.sort,
					label: 'Сортировка',
					resetOption: sortOptions.find(
						(option) => option.name === defaultFiltersValues.sort
					),
					onChange: setFilter,
					controlTitleField: 'title',
					valueField: 'name',
					alwaysHideResetOption: true
				}
			})
		});
	}, [filtersValues, setFilter, showSortFilter]);

	const switchFilter = (data, status) => {
		setFilterData(data);
		setFilterStatus(status);
	};

	const toggleModal = () => {
		setIsModalOpened((prev) => !prev);
	};

	const closeModal = () => {
		switchFilter(null, false);

		if (!filterData) {
			toggleModal();
		}
	};

	const resetOneFilter = (name) => {
		const filter = filterList[name];
		const omitFilters = _(filterOptions).omit(name);

		setFilterOptions(omitFilters);
		const resetValue = filter?.resetOption
			? filter?.resetOption[filter?.valueField || 'value']
			: defaultFiltersValues[name];

		onFilterChange(name, resetValue);
	};

	return (
		<>
			<div className={styles.MobileControls}>
				<Controls right={<FilterButton onClick={toggleModal} />}>
					<ControlsBlock className={styles.MobileControls__Block}>
						<Search
							onEnter={onFilterChange}
							defaultTitle="Поиск"
							placeholder=""
							value={filters.query}
						/>
					</ControlsBlock>

					<FilterModal
						modalStatus={isModalOpened}
						modalHandler={toggleModal}
						closeHandler={closeModal}
						label={filterData?.label}
					>
						<div className={styles.MobileControls__ModalContent}>
							{filterStatus && !_.isEmpty(filterData) ? (
								<>
									{filterList[filterData?.name]?.type === 'switches' ? (
										<div className={styles.MobileControls__ListSwitch}>
											{filterList[filterData?.name]?.filters.map((filter) => (
												<Switch
													key={filter.name}
													name={filter.name}
													title={filter.label}
													theme="simple"
													checked={filters?.[filter.name]}
													onChange={onFilterChange}
												/>
											))}
										</div>
									) : (
										<MobileSelect
											{...filterList[filterData.name]}
											filterOptions={filterOptions}
											selectedValue={filterOptions[filterData.name]}
											isLoading={filtersLoading}
										/>
									)}
								</>
							) : (
								!_.isEmpty(filtersValues) && (
									<MobileList
										{...{
											defaultFiltersValues,
											filterOptions,
											rubricPath,
											switchFilter,
											filters,
											filterList,
											onFilterChange,
											onFilterReset,
											resetOneFilter,
											onChange: setFilter,
											setModalStatus: setIsModalOpened
										}}
									/>
								)
							)}
						</div>
					</FilterModal>
				</Controls>
			</div>
		</>
	);
};

MobileInstituteControls.propTypes = {
	onFilterChange: PropTypes.func,
	onFilterReset: PropTypes.func,
	filters: PropTypes.shape({
		hasInfographic: PropTypes.bool,
		hasVirtualTours: PropTypes.bool,
		isCommercial: PropTypes.bool,
		location: PropTypes.string,
		query: PropTypes.string,
		sort: PropTypes.string,
		architect: PropTypes.string,
		type: PropTypes.string,
		subordinationType: PropTypes.string
	}),
	filtersValues: filtersValuesPropType,
	defaultFiltersValues: PropTypes.object,
	filtersLoading: PropTypes.bool,
	rubricPath: PropTypes.string,
	showSortFilter: PropTypes.bool
};

MobileInstituteControls.defaultProps = {
	onFilterChange: _.noop,
	filters: {},
	filtersValues: [],
	defaultFiltersValues: {}
};

export default MobileInstituteControls;
